import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Compact pager for admin list tables. Renders "Showing X–Y of Z" + Prev/Next
 * with a page-size selector. Emits new (limit, skip) via onChange.
 *
 * Props:
 *  - total    total row count (from paginated envelope)
 *  - limit    current page size
 *  - skip     current row offset
 *  - onChange({ limit, skip }) — parent updates its query state
 *  - testidPrefix (default: "pager")
 *  - pageSizes (default: [25, 50, 100, 200])
 */
export default function Pager({
  total = 0,
  limit = 50,
  skip = 0,
  onChange,
  testidPrefix = "pager",
  pageSizes = [25, 50, 100, 200],
}) {
  const start = total === 0 ? 0 : skip + 1;
  const end = Math.min(total, skip + limit);
  const canPrev = skip > 0;
  const canNext = end < total;

  const setLimit = (l) => onChange?.({ limit: Number(l), skip: 0 });
  const prev = () => onChange?.({ limit, skip: Math.max(0, skip - limit) });
  const next = () => onChange?.({ limit, skip: skip + limit });

  return (
    <div
      data-testid={testidPrefix}
      className="flex items-center justify-between flex-wrap gap-3 px-6 py-3 border-t border-white/5 bg-[#170929]/40"
    >
      <div className="text-xs text-slate-500">
        Showing{" "}
        <span data-testid={`${testidPrefix}-range`} className="font-mono text-slate-300">
          {start}–{end}
        </span>{" "}
        of{" "}
        <span data-testid={`${testidPrefix}-total`} className="font-mono text-slate-300">
          {total.toLocaleString("en-IN")}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <select
          data-testid={`${testidPrefix}-page-size`}
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          className="velo-input h-8 text-xs pl-2 pr-6 rounded-md bg-transparent border border-white/10 text-slate-300"
        >
          {pageSizes.map((s) => (
            <option key={s} value={s} className="bg-[#22103F]">
              {s} / page
            </option>
          ))}
        </select>
        <Button
          data-testid={`${testidPrefix}-prev`}
          size="sm"
          variant="outline"
          disabled={!canPrev}
          onClick={prev}
          className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5 h-8 px-3 disabled:opacity-30"
        >
          <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2} />
        </Button>
        <Button
          data-testid={`${testidPrefix}-next`}
          size="sm"
          variant="outline"
          disabled={!canNext}
          onClick={next}
          className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5 h-8 px-3 disabled:opacity-30"
        >
          <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
}
